import React, { useEffect, useRef } from 'react';
import { BookmarkIcon } from '@heroicons/react/24/outline';
import type { ChatSource } from '@archie/api-shared';
import SourceCard from './SourceCard';
import { sourceKey } from './sourceKey';

interface ConversationSourcesPanelProps {
  sources: ChatSource[];
  highlightedKey: string | null;
}

const ConversationSourcesPanel: React.FC<ConversationSourcesPanelProps> = ({ sources, highlightedKey }) => {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!highlightedKey || !listRef.current) return;
    const target = listRef.current.querySelector<HTMLElement>(`[data-source-key="${CSS.escape(highlightedKey)}"]`);
    if (target) {
      target.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [highlightedKey]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-(--color-border-default)">
        <BookmarkIcon className="h-4 w-4 text-(--color-text-muted)" />
        <h2 className="text-sm font-semibold text-(--color-text-primary)">Sources</h2>
        {sources.length > 0 && (
          <span className="ml-auto text-xs text-(--color-text-muted) bg-(--color-surface-subtle) rounded-full px-2 py-0.5">
            {sources.length}
          </span>
        )}
      </div>
      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {sources.length === 0 ? (
          <div className="text-sm text-(--color-text-muted) text-center px-4 py-8">
            No sources cited yet. Sources the assistant references will appear here.
          </div>
        ) : (
          sources.map((source, i) => {
            const key = sourceKey(source);
            return (
              <SourceCard key={key} source={source} index={i} sourceKey={key} highlighted={highlightedKey === key} />
            );
          })
        )}
      </div>
    </div>
  );
};

export default ConversationSourcesPanel;
